"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TurnrCard, TurnrCardContent, TurnrCardHeader } from "~/components/card/TurnrCard.jsx";
import { getOrderId } from "~/cores/orderCore";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { TurnrTableCell, TurnrTableRow } from "~/components/table/TurnrTableSmart.jsx";
import ReportItemRow, { ItemRowTitle } from "./ReportItemRow";
import { serviceIsFetched, serviceIsLoading, serviceIsLoadingOrNotFetched, serviceIsSuccessfullyFetched, useServiceHook } from "rylak-common";
import { fetchQualityOrderFull, preSubmitQualityCheckReport, submitQualityCheckReport } from "~/services";
import { COLORS } from "rylak-common/src/constants";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { TurnrFontAwesomeIcon } from "~/components/buttons/icon-buttons/TurnrFontAwesomeIconButton";
import { faBarcodeRead, faBox, faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import OrderInformationInfoBox from "~/components/infoBoxes/OrderInformationInfoBox.jsx";
import OrderNotesCard from "../../returns/[id]/OrderNotesCard";
import EntityAddressInfoBox from "~/components/infoBoxes/EntityAddressInfoBox.jsx";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import { TurnrTooltip } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import ReturnStatisticsCard from "~/components/charts/chartCommon/tooltips/ReturnStatisticsCard.jsx";
import AddItemModal from "./(addItemModal)/AddItemModal";
import TurnrConfirmSaveModalButton from "~/components/buttons/TurnrConfirmSaveModalButton.jsx";
import { TurnrServiceSnackbar } from "~/components/snackbars/TurnrServiceSnackbars.jsx";
import FormEntryPoint from "~/components/entityAction/form/FormEntryPoint";
import { SkeletonLoaderWrapper } from "~/components/loading/skeletons/SkeletonLoaderWrapper.jsx";
import TurnrModal, { TurnrModalContent } from "~/components/modals/TurnrModal.jsx";
import { TurnrAlert } from "~/components/alerts/TurnrAlert.jsx";
import { getReportedOrderItemId } from "~/cores/qualityCheckCore";
import ReportItemTable from "./ReportItemTable";
import _ from "lodash";
import HighlightText from "~/components/highlightText/HighlightText.jsx";

export default function CreateReportView({
    appProps,
    reportOnOrderId,
    onGoBackClicked,
    selectedReturnStation,
    highlightText,
    scanForProductValueModalOpen,
    scanForProductValue,
    setScanForProductValue,
}) {
    let { serviceData: qualityCheckOrderFullServiceData } = useServiceHook({
        appProps,
        fetchRequested: true,
        fetchData: {
            orderId: reportOnOrderId,
        },

        fetchFn: fetchQualityOrderFull,
    });

    let { serviceData: submitQualityCheckReportServiceData, fetchServiceData: fetchSubmitQualityCheckReportService } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: submitQualityCheckReport,
    });

    let { serviceData: preSubmitQualityCheckReportServiceData, fetchServiceData: fetchPreSubmitQualityCheckReportService } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: preSubmitQualityCheckReport,
    });

    useEffect(() => {
        if (serviceIsSuccessfullyFetched(submitQualityCheckReportServiceData)) {
            appProps?.reloadPage();
        }
    }, [submitQualityCheckReportServiceData]);

    const loading = serviceIsLoadingOrNotFetched(qualityCheckOrderFullServiceData);

    const selectedOrder = qualityCheckOrderFullServiceData?.data?.order;
    const [addedOrderItems, setAddedOrderItems] = useState([]);
    const [addedCustomItems, setAddedCustomItems] = useState([]);
    const [addItemModalOpen, setAddItemModalOpen] = useState(false);

    const [itemFormValuesMap, setItemFormValuesMap] = useState({});
    const [preSubmitFormValues, setPreSubmitFormValues] = useState({});

    function submitReport(fetchService = fetchSubmitQualityCheckReportService) {
        const items = Object.entries(itemFormValuesMap)
            .filter?.(([, values]) => !!values?.actionData)
            .map(([id, values]) => {
                const foundCustomItem = addedCustomItems?.find((customItem) => customItem?.qualityCheckItemId + "" === id + "");

                return {
                    qualityCheckItemId: foundCustomItem ? undefined : id,
                    customItemData: foundCustomItem?.data,
                    // type: "",
                    actionData: values?.actionData,
                };
            });

        fetchService({
            returnStation: selectedReturnStation,
            orderId: reportOnOrderId,
            items: items,
            forceReview: false,
            ...preSubmitFormValues,
        });
    }

    function preSubmitReport() {
        // we fetch with the exact same values as submit but with another service meant only for "calculations"
        submitReport(fetchPreSubmitQualityCheckReportService);
    }

    const setItemFormValues = useCallback(
        (orderItem, formValues) => {
            const id = getReportedOrderItemId(orderItem);
            itemFormValuesMap[id] = formValues;
            setItemFormValuesMap({ ...itemFormValuesMap });
        },
        [itemFormValuesMap, setItemFormValuesMap, getReportedOrderItemId],
    );

    const onRemoveOrderItemClick = useCallback(
        (item) => {
            const itemId = item?.qualityCheckItemId;
            const foundIndex = addedOrderItems?.findIndex((addedItem) => addedItem?.qualityCheckItemId === itemId);
            if (foundIndex > -1) {
                // only splice array when item is found
                addedOrderItems.splice(foundIndex, 1); // 2nd parameter means remove one item only
            }
            setAddedOrderItems([...addedOrderItems]);
            delete itemFormValuesMap[itemId];
            setItemFormValuesMap({ ...itemFormValuesMap });
        },

        [addedOrderItems, itemFormValuesMap, setItemFormValuesMap],
    );

    const onRemoveCustomItem = useCallback(
        (customItem) => {
            const itemId = customItem?.qualityCheckItemId;
            const foundIndex = addedCustomItems?.findIndex((addedItem) => addedItem?.qualityCheckItemId === itemId);
            if (foundIndex > -1) {
                // only splice array when item is found
                addedCustomItems.splice(foundIndex, 1); // 2nd parameter means remove one item only
            }
            setAddedCustomItems([...addedCustomItems]);
            delete itemFormValuesMap[itemId];
            setItemFormValuesMap({ ...itemFormValuesMap });
        },

        [setAddedCustomItems, itemFormValuesMap, setItemFormValuesMap, addedCustomItems],
    );

    const onAddCustomItem = useCallback(
        (itemData) => {
            setAddedCustomItems([...addedCustomItems, { qualityCheckItemId: Math.random(), data: itemData }]);
        },
        [setAddedCustomItems, addedCustomItems],
    );

    const onAddItem = useCallback(
        (item) => {
            setAddedOrderItems([...addedOrderItems, item]);
        },
        [addedOrderItems],
    );

    const allSelectedItems = addedOrderItems?.concat(addedCustomItems);

    const nrOfReportedItems = Object.values(itemFormValuesMap).filter((value) => !!value?.actionData).length;

    const atLeastOneItemReported = nrOfReportedItems > 0; //&& nrOfReportedItems === nrOfItemsToRender;

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    function onToggleConfirmModal(open) {
        if (open) {
            preSubmitReport();
        }
        setConfirmModalOpen(open);
    }

    useEffect(() => {
        if (serviceIsFetched(preSubmitQualityCheckReportServiceData)) {
            // We make sure to reset
            setPreSubmitFormValues({});
        }
    }, [preSubmitQualityCheckReportServiceData]);

    const preSubmitLoading = serviceIsLoading(preSubmitQualityCheckReportServiceData) || !preSubmitFormValues;

    const [changingValuesByScanningKey, setChangingValuesByScanningKey] = useState(0);

    const scannedReturnOrShipmentRows = useMemo(() => {
        return qualityCheckOrderFullServiceData?.data?.rows
            ?.map(({ orderReturn, shipment }) => {
                const items = (shipment?.items || orderReturn?.items)?.filter(
                    (item) => (item?.productVariant?.sku === scanForProductValue || item?.productVariant?.barCode === scanForProductValue) && !item?.alreadyHandled,
                );
                let newObject = {
                    orderReturn: _.clone(orderReturn),
                    shipment: _.clone(shipment),
                };
                if (shipment) {
                    newObject.shipment.items = items;
                }
                if (orderReturn) {
                    newObject.orderReturn.items = items;
                }

                return newObject;
            })
            ?.filter(({ orderReturn, shipment }) => (orderReturn?.items || shipment?.items)?.length);
    }, [qualityCheckOrderFullServiceData, scanForProductValue]);

    const scannedOrderItems = useMemo(
        () =>
            qualityCheckOrderFullServiceData?.data?.itemsWithoutReturn.filter(
                (item) => (item?.productVariant?.sku === scanForProductValue || item?.productVariant?.barCode === scanForProductValue) && !item?.alreadyHandled,
            ),
        [qualityCheckOrderFullServiceData, scanForProductValue],
    );

    const allScannedHits = useMemo(
        () => scannedReturnOrShipmentRows?.flatMap(({ orderReturn, shipment }) => shipment?.items || orderReturn?.items).concat(scannedOrderItems),
        [scannedReturnOrShipmentRows, scannedOrderItems],
    );

    const scannedSetFormValues = useCallback(
        (orderItem, formValues) => {
            if (orderItem?.type === "ORDER_LINE_ITEM" && !addedOrderItems?.find((item) => item?.qualityCheckItemId === orderItem?.qualityCheckItemId)) {
                onAddItem(orderItem);
            }
            setItemFormValues(orderItem, formValues);
            setChangingValuesByScanningKey(changingValuesByScanningKey + 1);

            if (allScannedHits?.length === 1 && formValues?.actionData) {
                setScanForProductValue(null);
            }
        },
        [onAddItem, setItemFormValues, changingValuesByScanningKey, setChangingValuesByScanningKey, itemFormValuesMap, allScannedHits],
    );

    return (
        <>
            <TurnrModal open={scanForProductValueModalOpen} onClose={setScanForProductValue}>
                <TurnrModalContent>
                    {allScannedHits?.length < 1 ? (
                        <TurnrAlert fullWidth={true}>
                            No product found with ID: <HighlightText highlightText={scanForProductValue} text={scanForProductValue} />, or the product has already been processed. If you're searching
                            for packages again, click once on the search field.
                        </TurnrAlert>
                    ) : (
                        <>
                            <ReportItemTable
                                appProps={appProps}
                                selectedOrder={selectedOrder}
                                returnOrShipmentRows={scannedReturnOrShipmentRows}
                                orderItems={scannedOrderItems}
                                itemFormValuesMap={itemFormValuesMap}
                                setItemFormValues={scannedSetFormValues}
                                highlightText={scanForProductValue}
                            />
                        </>
                    )}
                </TurnrModalContent>
            </TurnrModal>
            <TurnrServiceSnackbar appProps={appProps} serviceData={submitQualityCheckReportServiceData} successTitle={"Successfully submitted report"} />
            <TurnrGrid container spacing={2}>
                <TurnrGrid container item xs={12} spacing={2}>
                    <TurnrGrid item xs={12} xl={8}>
                        <TurnrGrid container spacing={2} alignItems={"center"} direction={"row"} justifyContent={"space-between"}>
                            <TurnrGrid item>
                                <TurnrGrid container spacing={2} alignItems={"center"} justifyContent={"center"}>
                                    <TurnrGrid item>
                                        <TurnrTooltip
                                            title={"Show search result view"}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <TurnrButton onClick={onGoBackClicked} fontAwesomeIcon={faChevronLeft} variant={"text"} size={"small"}>
                                                Go back
                                            </TurnrButton>
                                        </TurnrTooltip>
                                    </TurnrGrid>
                                </TurnrGrid>
                            </TurnrGrid>
                            <TurnrGrid item>
                                <TurnrGrid container spacing={3} alignItems={"center"}>
                                    <TurnrGrid item>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "5px",
                                            }}
                                        >
                                            <TurnrFontAwesomeIcon icon={faBarcodeRead} color={COLORS.WEAK_TEXT} />
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    color: COLORS.WEAK_TEXT,
                                                }}
                                            >
                                                Scan a product from this order
                                            </div>
                                        </div>
                                    </TurnrGrid>
                                    <TurnrGrid item>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "5px",
                                            }}
                                        >
                                            <TurnrFontAwesomeIcon icon={faBox} color={COLORS.WEAK_TEXT} />
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    color: COLORS.WEAK_TEXT,
                                                }}
                                            >
                                                Check all items before submitting report
                                            </div>
                                        </div>
                                    </TurnrGrid>

                                    <TurnrGrid item>
                                        <TurnrTooltip content={!atLeastOneItemReported && "You have to give a report on at least one item 1"}>
                                            <TurnrConfirmSaveModalButton
                                                title={"Submit report"}
                                                open={confirmModalOpen}
                                                confirmButtonDisabled={serviceIsLoading(preSubmitQualityCheckReportServiceData)}
                                                setOpen={onToggleConfirmModal}
                                                text={
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            gap: "20px",
                                                            flexDirection: "column",
                                                            width: "100%",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        <SkeletonLoaderWrapper loadingMinHeight={"135px"} loading={preSubmitLoading}>
                                                            {serviceIsSuccessfullyFetched(preSubmitQualityCheckReportServiceData) && !preSubmitLoading && (
                                                                <div style={{ textAlign: "left" }}>
                                                                    <FormEntryPoint
                                                                        formParameters={preSubmitQualityCheckReportServiceData?.data?.submitForm?.formParameters}
                                                                        formValues={preSubmitFormValues}
                                                                        setFormValues={setPreSubmitFormValues}
                                                                        loading={serviceIsLoading(submitQualityCheckReportServiceData, preSubmitQualityCheckReportServiceData)}
                                                                        appProps={appProps}
                                                                    />
                                                                </div>
                                                            )}
                                                        </SkeletonLoaderWrapper>
                                                    </div>
                                                }
                                                style={{ marginLeft: "auto" }}
                                                disabled={!atLeastOneItemReported}
                                                serviceData={submitQualityCheckReportServiceData}
                                                color={COLORS.ACTION}
                                                confirmButtonText={"Submit"}
                                                size={"small"}
                                                onSubmit={submitReport}
                                            >
                                                Submit Report
                                            </TurnrConfirmSaveModalButton>
                                        </TurnrTooltip>
                                    </TurnrGrid>
                                </TurnrGrid>
                            </TurnrGrid>
                        </TurnrGrid>
                    </TurnrGrid>

                    {/* Table part */}
                    <TurnrGrid item xs={12} xl={8}>
                        {serviceIsLoadingOrNotFetched(qualityCheckOrderFullServiceData) ? (
                            <TurnrCard loading={true} loadingMinHeight={"400px"} />
                        ) : (
                            <>
                                <ReportItemTable
                                    appProps={appProps}
                                    selectedOrder={selectedOrder}
                                    returnOrShipmentRows={qualityCheckOrderFullServiceData?.data?.rows}
                                    orderItems={addedOrderItems}
                                    itemFormValuesMap={itemFormValuesMap}
                                    setItemFormValues={setItemFormValues}
                                    highlightText={highlightText}
                                    onRemoveOrderItem={(orderItem) => onRemoveOrderItemClick(orderItem)}
                                >
                                    {addedCustomItems?.length > 0 && <ItemRowTitle title={"Custom items"} />}
                                    {addedCustomItems?.map((customItem) => {
                                        return (
                                            <ReportItemRow
                                                key={getReportedOrderItemId(customItem)}
                                                appProps={appProps}
                                                order={selectedOrder}
                                                actionFormButton={qualityCheckOrderFullServiceData?.data?.addCustomItemActionButton}
                                                customItem={customItem}
                                                formValues={itemFormValuesMap[getReportedOrderItemId(customItem)]}
                                                setFormValues={(formValues) => setItemFormValues(customItem, formValues)}
                                                onRemoveClick={() => onRemoveCustomItem(customItem)}
                                            />
                                        );
                                    })}
                                    <TurnrTableRow>
                                        <TurnrTableCell colSpan={4} fullWidth={true}>
                                            <TurnrButton fullWidth={true} variant={"text"} style={{ height: "50px" }} onClick={() => setAddItemModalOpen(true)}>
                                                + Add another item
                                            </TurnrButton>
                                        </TurnrTableCell>
                                    </TurnrTableRow>
                                </ReportItemTable>
                            </>
                        )}
                    </TurnrGrid>

                    {/* Right side */}
                    <TurnrGrid item xs={12} xl={4}>
                        <TurnrGrid container spacing={1}>
                            <TurnrGrid item xs={12}>
                                <TurnrCard style={{ borderRadius: 0 }} loading={loading}>
                                    <TurnrCardHeader>Order</TurnrCardHeader>
                                    <TurnrCardContent>
                                        <OrderInformationInfoBox appProps={appProps} order={selectedOrder} highlightText={highlightText} />
                                    </TurnrCardContent>
                                    {selectedOrder && <OrderNotesCard appProps={appProps} orderId={getOrderId(selectedOrder)} style={{ height: "100%" }} />}
                                </TurnrCard>
                            </TurnrGrid>
                            <TurnrGrid item xs={12}>
                                <TurnrCard style={{ borderRadius: 0 }} loading={loading}>
                                    <TurnrCardHeader title={"Buyer"} />
                                    <TurnrCardContent>
                                        <EntityAddressInfoBox size={"small"} entityAddress={selectedOrder?.address} customer={selectedOrder?.customer} highlightText={highlightText} />
                                    </TurnrCardContent>
                                    <LineDivider />
                                    <TurnrCardContent>
                                        <ReturnStatisticsCard customer={selectedOrder?.customer} showValue={false} />
                                    </TurnrCardContent>
                                    <LineDivider />
                                </TurnrCard>
                            </TurnrGrid>
                        </TurnrGrid>
                    </TurnrGrid>
                </TurnrGrid>
            </TurnrGrid>
            <AddItemModal
                appProps={appProps}
                open={addItemModalOpen}
                onClose={setAddItemModalOpen}
                qualityCheckOrderFullServiceData={qualityCheckOrderFullServiceData}
                onAddItem={onAddItem}
                onAddCustomItem={onAddCustomItem}
                onRemoveItem={onRemoveOrderItemClick}
                onRemoveCustomItem={onRemoveCustomItem}
                allSelectedItems={allSelectedItems}
                // reportOnReturnId={reportOnReturnId}
                // reportOnShipmentId={reportOnShipmentId}
                addedCustomItems={addedCustomItems}
            />
        </>
    );
}
